import { ADynamicFormField } from '@acaprojects/ngx-dynamic-forms';
import { Validators } from '@angular/forms';

import { BaseDataClass } from '../base-api.class';
import { MapObject } from '../../../shared/utilities/types.utilities';
import { Space } from '../spaces/space.class';
import { BaseAPIService } from '../base.service';

import * as dayjs from 'dayjs';

export interface ILocation {
    x?: number;
    y?: number;
    name?: string;
    map_id?: string;
    building: string;
    level: string | { id: string, name: string };
    fixed: boolean;
    loc_id?: string;
    display?: { [name: string]: string};
    confidence?: number;
    another_bld?: boolean;
    at_desk?: boolean;
}

export type IUserType = 'external' | 'av-test' | 'another provider' | 'carer' | 'interpreter' | 'clinician' | 'patient' | 'admin' | 'employee';

export class User extends BaseDataClass {
    /** Quality result of the last audio test */
    public audio_tested: 'good' | 'bad' | 'none';
    /** Quality result of the last video test */
    public video_tested: 'good' | 'bad' | 'none';

    /** Title of the user */
    readonly title: string;
    /** First name of the user */
    readonly first_name: string;
    /** Last name of the user */
    readonly last_name: string;
    /** Windows user ID */
    readonly windows_id: string;
    /** Pexip Meeting ID */
    readonly pexip_id: string;
    /** Pexip Meeting Pin */
    readonly pexip_pin: string;
    /** Type of user */
    readonly type: IUserType;
    /** Contact phone number of the user */
    readonly phone: string;
    /** Organisational department the user is part of */
    readonly business_unit: string;
    /** Whether user is external from the application's organisation */
    readonly external: boolean;
    /** Organisational Identifier for the user */
    readonly staff_code: string;
    /** Time that the user has an appointment */
    readonly appointment_time: number;
    /** Whether to send notification via phone */
    readonly phone_notification: boolean;
    /** Whether to send notifications via email */
    readonly phone_email: boolean;
    /** Whether to send notification via the browser window */
    readonly browser_notification: boolean;
    /** Language of user */
    readonly language: string;
    /** ID of the applicable clinician for the user */
    private _clinician: string;
    /** ID of the applicable patient that the user is here for */
    private _patient: string;
    /** ID of the User's waiting room */
    private _waiting_room: string
    /** Organisation the user is associated with */
    private _organisation: { id: string, name: string };
    /** Map location of the user  */
    private _location: ILocation;
    /** List of users that this user can act on behalf */
    private _delegates: string[];
    /** Link for welcome */
    readonly welcome_video: string;
    /** Link for exit survey */
    readonly exit_survey: string;
    /** Pexip Url */
    readonly pexip_url: string;

    constructor(protected service: BaseAPIService<User>, raw_data: MapObject = {}) {
        super(service, raw_data);
        if (!raw_data) { raw_data = {}; }
        this.title = raw_data.title;
        this.first_name = raw_data.first_name;
        this.last_name = raw_data.last_name;
        this.windows_id = raw_data.email;
        this.type = raw_data.visitor ? 'another provider' : raw_data.type || raw_data.role;
        this.phone = raw_data.phone || raw_data.mobile;
        this.business_unit = raw_data.business_unit || raw_data.department;
        this._organisation = raw_data.organisation || { id: raw_data.organisation_id, name: raw_data.organisation_name };
        this.external = this.type === 'external' || this.type === 'another provider';
        this.staff_code = raw_data.staff_code || raw_data.fmno;
        this._delegates = raw_data.delegates;
        this.audio_tested = raw_data.audio_tested;
        this.video_tested = raw_data.video_tested;
        this.appointment_time = raw_data.appointment_time;
        this._waiting_room = raw_data._waiting_room || raw_data.room;
        this.language = raw_data.language;
        this.pexip_id = raw_data.pexip_id || raw_data.meeting_id;
        this.pexip_pin = raw_data.pexip_pin || raw_data.meeting_pin;
        this._clinician = raw_data.clinician ? raw_data.clinician.id : raw_data.clinician_id;
        this.welcome_video = raw_data.welcome_video;
        this.exit_survey = raw_data.exit_survey;
        this.pexip_url = raw_data.pexip_url;
        if (raw_data.clinician && !(raw_data.clinician instanceof User) && !this.service.item(raw_data.clinician.id)) {
            this.service.addFrom(this.id, raw_data.clinician, 'class');
        }
        this._patient = raw_data.patient ? raw_data.patient.id : raw_data.patient_id;
        if (raw_data.patient && !(raw_data.patient instanceof User) && !this.service.item(raw_data.patient.id)) {
            this.service.addFrom(this.id, raw_data.patient, 'class');
        }
    }

    /** Organisation the user is associated with */
    public get organisation() {
        return this._organisation;
    }

    /** Map location of the user */
    public get last_location(): ILocation {
        return this._location ? { ...(this._location) } : null;
    }

    /** Display name of the user */
    public get display_name(): string {
        return `${this.name} (${this.type})`;
    }

    /**
     * As the server for the users current location
     */
    public locate(): Promise<ILocation> {
        const service: any = this.service.parent || {};
        return new Promise((resolve, reject) => {
            if (service.Location) {
                service.Location.show(this.id, { desk: this.email }).then(
                    l => {
                        this._location = l;
                        resolve(l);
                    },
                    e => reject(e)
                );
            } else {
                resolve(null);
            }
        });
    }

    /** List of users that this user can act on behalf */
    public get delegates(): string[] {
        return [...(this._delegates || [])];
    }

    /** Applicable clinician for the user */
    public get clinician(): User {
        return this.service.item(this._clinician);
    }

    /** Applicable patient that the user is here for */
    public get patient(): User {
        return this.service.item(this._patient);
    }

    /** Waiting Room associated with the user */
    public get waiting_room(): Space {
        const id = this._waiting_room;
        return this.service.parent.Spaces.item(id)
            || new Space(this.service.parent.Spaces, { id, name: id, email: id });
    }

    /** Human readable string for the user's appointment time */
    public get appointment_at(): string {
        return this.appointment_time ? dayjs(this.appointment_time).format('h:mm A') : '';
    }

    /** Time in minutes until the appointment time */
    public get wait_time(): number {
        const now = dayjs();
        const time = dayjs(this.appointment_time);
        return time.diff(now, 'm');
    }

    /**
     * Whether user is available during the given period
     * @param start Start time as a unix timestamp with milliseconds
     * @param end End time as a unix timestamp with milliseconds
     */
    public available(start?: number, end?: number): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            this.service.query({ email: this.email, start, end })
                .then(l => resolve(l && l.length > 0), _ => resolve(false));
        });
    }

    /**
     * Make a copy of this object
     */
    public clone(): User {
        return new User(this.service, this);
    }

    /**
     * Make a copy of this object without identification data
     */
    public duplicate(): User {
        return new User(this.service, { ...this, id: null, email: null });
    }

    public toString(): string {
        return JSON.stringify({
            id: this.id,
            name: this.name,
            type: this.type,
            pexip_id: this.pexip_id,
            pexip_pin: this.pexip_pin,
            pexip_url: this.pexip_url,
            welcome_video: this.welcome_video
        });
    }

    /**
     * Initialise the form field metadata for the field
     */
    protected initialiseFormFields(): ADynamicFormField[] {
        const edit = !!this.id;
        const fields: ADynamicFormField[] = [
            {
                key: 'name',
                type: 'input',
                value: this.name,
                hide: true,
                required: true
            }, {
                key: 'title',
                label: 'Title',
                type: 'input',
                value: this.title
            }, {
                key: 'phone_notification',
                label: 'Phone Notification',
                type: 'toggle',
                value: this.phone_notification
            }, {
                key: 'phone_email',
                label: 'Phone Email',
                type: 'toggle',
                value: this.phone_email
            }, {
                key: 'browser_notification',
                label: 'Browser Notification',
                type: 'toggle',
                value: this.browser_notification
            }, {
                key: 'phone',
                label: 'Phone',
                type: 'input',
                value: this.phone,
                attributes: { type: 'tel' }
            }, {
                key: 'first_name',
                label: 'First Name',
                type: 'input',
                value: this.first_name,
                required: true
            }, {
                key: 'last_name',
                label: 'Last Name',
                type: 'input',
                value: this.last_name,
                required: true
            }, {
                key: 'email',
                label: 'Email',
                type: 'input',
                value: this.email,
                required: true,
                attributes: { type: 'email' },
                validators: [
                    Validators.email
                ]
            }, {
                key: 'language',
                label: 'Language',
                type: 'input',
                value: this.language
            }
        ].map(i => new ADynamicFormField(i as any));
        return fields;
    }
}
